import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { projectService, type ProjectInvoice } from '@services/projectService';
import { normalizeInvoice } from '../normalizers/invoiceNormalizers';
import {
  ensureProjectBucket,
  isProjectBucketFresh,
  markBucketError,
  markBucketLoading,
  type ProjectBucket,
} from '../utils/projectBucket';
import type { RootState } from '../index';

interface ProjectInvoicesBucket extends ProjectBucket {
  items: ProjectInvoice[];
  isSaving: boolean;
}

interface ProjectInvoicesState {
  byProjectId: Record<string, ProjectInvoicesBucket>;
}

const createBucket = (): ProjectInvoicesBucket => ({
  items: [],
  isSaving: false,
  isLoading: false,
  error: null,
  lastFetched: null,
});

const initialState: ProjectInvoicesState = {
  byProjectId: {},
};

export const fetchProjectInvoices = createAsyncThunk(
  'projectInvoices/fetchProjectInvoices',
  async (
    { projectId, force }: { projectId: string; force?: boolean },
    { getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const bucket = state.projectInvoices.byProjectId[projectId];

    if (isProjectBucketFresh(bucket, force)) {
      return { projectId, items: null as ProjectInvoice[] | null };
    }

    try {
      const fetched = await projectService.getInvoices(projectId);
      const items = fetched.map(invoice =>
        normalizeInvoice(invoice as unknown as Record<string, unknown>, { id: projectId, name: '' })
      );
      return { projectId, items };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load project invoices';
      return rejectWithValue({ projectId, message });
    }
  }
);

export const createProjectInvoice = createAsyncThunk(
  'projectInvoices/create',
  async (
    { projectId, data }: { projectId: string; data: Omit<ProjectInvoice, 'id'> },
    { rejectWithValue }
  ) => {
    try {
      const invoice = await projectService.createInvoice(projectId, data);
      return { projectId, invoice };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create invoice');
    }
  }
);

const projectInvoicesSlice = createSlice({
  name: 'projectInvoices',
  initialState,
  reducers: {
    // Keep the cache in sync after a mutation completes in a component.
    upsertProjectInvoice: (
      state,
      action: PayloadAction<{ projectId: string; invoice: ProjectInvoice }>
    ) => {
      const bucket = ensureProjectBucket(state.byProjectId, action.payload.projectId, createBucket);
      const index = bucket.items.findIndex(invoice => invoice.id === action.payload.invoice.id);
      if (index >= 0) {
        bucket.items[index] = action.payload.invoice;
      } else {
        bucket.items.unshift(action.payload.invoice);
      }
    },
    removeProjectInvoice: (
      state,
      action: PayloadAction<{ projectId: string; invoiceId: string }>
    ) => {
      const bucket = state.byProjectId[action.payload.projectId];
      if (!bucket) return;
      bucket.items = bucket.items.filter(invoice => invoice.id !== action.payload.invoiceId);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchProjectInvoices.pending, (state, action) => {
        const bucket = ensureProjectBucket(
          state.byProjectId,
          action.meta.arg.projectId,
          createBucket
        );
        markBucketLoading(bucket);
      })
      .addCase(fetchProjectInvoices.fulfilled, (state, action) => {
        const bucket = ensureProjectBucket(
          state.byProjectId,
          action.payload.projectId,
          createBucket
        );
        bucket.isLoading = false;
        if (action.payload.items !== null) {
          bucket.items = action.payload.items;
          bucket.lastFetched = Date.now();
        }
      })
      .addCase(fetchProjectInvoices.rejected, (state, action) => {
        const payload = action.payload as { projectId?: string; message?: string } | undefined;
        const projectId = payload?.projectId ?? action.meta.arg.projectId;
        const bucket = ensureProjectBucket(state.byProjectId, projectId, createBucket);
        markBucketError(bucket, payload?.message ?? 'Failed to load project invoices');
      })
      .addCase(createProjectInvoice.pending, (state, action) => {
        const bucket = ensureProjectBucket(
          state.byProjectId,
          action.meta.arg.projectId,
          createBucket
        );
        bucket.isSaving = true;
        bucket.error = null;
      })
      .addCase(createProjectInvoice.fulfilled, (state, action) => {
        const bucket = ensureProjectBucket(
          state.byProjectId,
          action.payload.projectId,
          createBucket
        );
        bucket.isSaving = false;
        bucket.items.unshift(action.payload.invoice);
      })
      .addCase(createProjectInvoice.rejected, (state, action) => {
        const bucket = ensureProjectBucket(
          state.byProjectId,
          action.meta.arg.projectId,
          createBucket
        );
        bucket.isSaving = false;
        bucket.error =
          typeof action.payload === 'string' ? action.payload : 'Failed to create invoice';
      });
  },
});

export const { upsertProjectInvoice, removeProjectInvoice } = projectInvoicesSlice.actions;

export const selectProjectInvoices = (state: RootState, projectId: string) =>
  state.projectInvoices.byProjectId[projectId]?.items ?? [];

export const selectProjectInvoicesLoading = (state: RootState, projectId: string) =>
  state.projectInvoices.byProjectId[projectId]?.isLoading ?? false;

export const selectProjectInvoicesSaving = (state: RootState, projectId: string) =>
  state.projectInvoices.byProjectId[projectId]?.isSaving ?? false;

export default projectInvoicesSlice.reducer;
